import { useState, useCallback, Dispatch, SetStateAction, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { ChannelFeaturesEnum, EPermissions } from "../../../types/working-model";
import Icon, { COLORS, ICONS } from '../../general-ui/icon';
import { AuthWrapper } from "../authentication/AuthWrapper";
import { PATHNAMES } from "../../../utils/admin-routing-utils";
import WaitingIndicator from "../../general-ui/waiting-indicator/waiting-indicator";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { ProfileDetails } from '../../general-ui/profile-details/profile-details';
import { OptionalComponent } from '../../../utils/optional-component';
import AdminNotifications from './admin-notifications/admin-notifications';
import ChannelSelectorV2 from './channel-selection/channel-selector-v2';
import CreateEventButton from '@admin/events/create-event-button';
import { useProjectDetailsEnabled } from '@admin/events/hooks/project-details';
import { selectChannelFlag } from 'store/selectors/channel-flags';

import './admin-top-nav.scss';
import { isWebinarChannel } from 'utils/project-type-guards';
import images from 'images';

const BRAND_TV_DOMAIN = process.env.REACT_APP_BRAND_TV_DOMAIN;

interface IAdminTopNav {
	noOptions?: boolean
	setShowEventGroupModal?: Dispatch<SetStateAction<boolean>>;
}

function AdminTopNav({ noOptions, setShowEventGroupModal }: IAdminTopNav): JSX.Element {
	const user = useTypedSelector(state => state.AuthReducer.user);
	const channels = useTypedSelector(state => state.AuthReducer.channels);

	const brandTVEnabled = useTypedSelector(selectChannelFlag(ChannelFeaturesEnum.brand_tv));
	const projectDetailsFlagOn = useProjectDetailsEnabled();

	const location = useLocation();
	const { pathname } = location;

	const [profileDetailsOpen, setProfileDetailsOpen] = useState(false);

	const getInitials = useCallback(() => {
		if (user && (user.profile.first_name || user.profile.last_name)) {
			return `${user.profile.first_name?.[0] ?? ''}${user.profile.last_name?.[0] ?? ''}`;
		} else {
			return user?.email?.[0];
		}
	}, [user]);

	const currentChannel = useMemo(() =>
		channels.find(channel => channel.channel === user?.active_channel),
		[user?.active_channel, channels]);

	return user ? (
		<div className={classNames('admin-top-nav', { 'has-project-details-enabled': projectDetailsFlagOn })}>
			<div className="left">
				<OptionalComponent display={projectDetailsFlagOn}>
					<ChannelSelectorV2 />
					<OptionalComponent display={!!currentChannel?.type}>
						<div>
							{isWebinarChannel(currentChannel?.type)
								? <img className="webinar-plus-logo" src={images.WebinarPlusLogo} />
								: <img className="brandlive-logo-2" src={images.BrandLiveLogo2} />}
						</div>
					</OptionalComponent>
				</OptionalComponent>
				<OptionalComponent display={!noOptions}>
					<AuthWrapper allowedRoles={[EPermissions.Moderation, EPermissions.Producer, EPermissions.Editor, EPermissions.GroupAdmin, EPermissions.Viewer, EPermissions.Presenter, EPermissions.PasscodeManager]}>
						<Link to={PATHNAMES.Events}>
							<div className={classNames('admin-top-nav-item', { active: pathname === '/admin/events' })}>
								Projects
								<OptionalComponent display={!projectDetailsFlagOn}>
									<div className="gradient-nav-item"></div>
								</OptionalComponent>
							</div>
						</Link>
					</AuthWrapper>
					<AuthWrapper allowedRoles={[EPermissions.PasscodeManager, EPermissions.GroupAdmin]}>
						<Link to={PATHNAMES.People}>
							<div className={classNames('admin-top-nav-item', { active: pathname.includes('people') })}>
								Audiences
								<OptionalComponent display={!projectDetailsFlagOn}>
									<div className="gradient-nav-item"></div>
								</OptionalComponent>
							</div>
						</Link>
					</AuthWrapper>
					<AuthWrapper allowedRoles={[EPermissions.Editor, EPermissions.GroupAdmin]}>
						<Link to={PATHNAMES.ContentSubLevels.Documents.All}>
							<div className={classNames('admin-top-nav-item', { active: pathname.includes('content') || pathname.includes('products') })}>
								Content
								<OptionalComponent display={!projectDetailsFlagOn}>
									<div className="gradient-nav-item"></div>
								</OptionalComponent>
							</div>
						</Link>
					</AuthWrapper>
					<AuthWrapper allowedRoles={[EPermissions.Analytics, EPermissions.GroupAdmin, EPermissions.Builder]}>
						<Link to={PATHNAMES.AnalyticsLink}>
							<div className={classNames('admin-top-nav-item', { active: pathname.includes('analytics') })}>
								Analytics
								<OptionalComponent display={!projectDetailsFlagOn}>
									<div className="gradient-nav-item"></div>
								</OptionalComponent>
							</div>
						</Link>
					</AuthWrapper>
					<AuthWrapper allowedRoles={[EPermissions.Owner, EPermissions.Admin, EPermissions.GroupAdmin]}>
						<Link to={PATHNAMES.SettingsLink}>
							<div className={classNames('admin-top-nav-item', { active: pathname.includes('settings') || pathname.includes('integrations') })}>
								Settings
								<OptionalComponent display={!projectDetailsFlagOn}>
									<div className="gradient-nav-item"></div>
								</OptionalComponent>
							</div>
						</Link>
					</AuthWrapper>
					<OptionalComponent display={brandTVEnabled && !!BRAND_TV_DOMAIN}>
						<a href={`https://app.${BRAND_TV_DOMAIN}`}>
							<div className={classNames('admin-top-nav-item', { active: pathname.includes('people') })}>
								BrandTV
								<OptionalComponent display={!projectDetailsFlagOn}>
									<div className="gradient-nav-item"></div>
								</OptionalComponent>
							</div>
						</a>
					</OptionalComponent>
				</OptionalComponent>
			</div>
			<div className="right">

				<OptionalComponent display={projectDetailsFlagOn}>
					<CreateEventButton setShowEventGroupModal={setShowEventGroupModal} />
				</OptionalComponent>
				<AdminNotifications />
				<button className="no-style profile-button" onClick={() => setProfileDetailsOpen(!profileDetailsOpen)}>
					<div className="profile-pic">
						{user?.profile?.picture ? (
							<img
								src={user.profile.picture}
								alt={user.profile.first_name + ' ' + user.profile.last_name}
							/>
						) : (
							<div className="user-initials">
								{getInitials()}
							</div>
						)}
					</div>
					<div className="profile-pic-arrow">
						<Icon
							name={profileDetailsOpen ? ICONS.KEYBOARD_ARROW_UP : ICONS.KEYBOARD_ARROW_DOWN}
							size={12}
							color={COLORS.WHITE}
						/>
					</div>
				</button>
				<ProfileDetails
					open={profileDetailsOpen}
					account={user}
					onClose={() => setProfileDetailsOpen(false)}
				/>
			</div>
		</div>
	) : (
		<WaitingIndicator minHeight={'90vh'} fillSpace={true} />
	);
}

export default AdminTopNav;
